import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { LogoLockupLight, BoutiqueStaysLockupLight } from "./Logo";
import { brand } from "@/lib/brand";

// Swap in the real App Store URL once BlueKeysApp is live on the App Store.
// Format: https://apps.apple.com/app/id<YOUR_APP_ID>
const APP_STORE_URL = "https://apps.apple.com/eg/app/bluekeys/id6776902486";

// Sync (NOT async): next-intl's `useTranslations` works in Server AND Client
// components, so Footer renders both in the server layout/not-found AND inside
// the client error boundary (`error.tsx`). An async Server Component cannot be
// a child of a Client Component, so getTranslations/await must not be used here.
export default function Footer() {
  const t = useTranslations("footer");
  const tc = useTranslations("common");

  // Brand-specific strings — bypass i18n so identity copy isn't in the i18n file.
  const tagline =
    brand.id === "boutique-stays"
      ? "Premium accommodation for corporate teams across Egypt — verified properties, flexible billing, and a dedicated account manager."
      : t("tagline");
  const copyright =
    brand.id === "boutique-stays" ? brand.copyright : t("copyright");

  return (
    <footer className="bg-ss-text text-[#a4b2c4] pt-14 pb-7">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-10">
          <div className="col-span-2">
            <div className="mb-3">
              {brand.id === "boutique-stays" ? (
                <BoutiqueStaysLockupLight size={22} />
              ) : (
                <LogoLockupLight size={22} />
              )}
            </div>
            <p className="text-sm leading-relaxed max-w-sm">{tagline}</p>
            {brand.id !== "boutique-stays" && (
              <>
                <div className="mt-4">
                  <span className="block text-white font-bold text-[13px] uppercase tracking-wider mb-2.5">
                    {t("followUs")}
                  </span>
                  <div className="flex items-center gap-3">
                    <a
                      href="https://www.instagram.com/bluekeys.co/"
                      target="_blank"
                      rel="me noopener noreferrer"
                      aria-label={t("instagramAriaLabel")}
                      className="hover:text-white transition-colors"
                    >
                      <InstagramIcon />
                    </a>
                    <a
                      href="https://www.tiktok.com/@bluekeys.co"
                      target="_blank"
                      rel="me noopener noreferrer"
                      aria-label={t("tiktokAriaLabel")}
                      className="hover:text-white transition-colors"
                    >
                      <TikTokIcon />
                    </a>
                  </div>
                </div>
                <div className="mt-5">
                  <span className="block text-white font-bold text-[13px] uppercase tracking-wider mb-2.5">
                    {t("getTheApp")}
                  </span>
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t("appStoreAriaLabel")}
                    className="inline-flex items-center gap-2 bg-black border border-white/20 rounded-[9px] px-3 py-2 hover:border-white/50 transition-colors"
                  >
                    <AppleIcon />
                    <span className="flex flex-col leading-tight">
                      <span className="text-[9px] text-white/60 font-medium">Download on the</span>
                      <span className="text-[13px] text-white font-semibold">App Store</span>
                    </span>
                  </a>
                </div>
              </>
            )}
          </div>
          {/* The region hubs, guides and two of the three price-index reports
              had NO inbound link from anywhere on the site — reachable only via
              sitemap.xml, or three clicks deep through the one price-index link
              that was here. The footer is on every page, so it is the cheapest
              way to give all of them a real route in. Booking path untouched. */}
          <FooterCol
            title={t("regionsTitle")}
            links={[
              { label: t("northCoast"), href: "/destinations/north-coast" },
              { label: t("cairo"), href: "/destinations/cairo" },
              { label: t("elGouna"), href: "/destinations/el-gouna" },
              { label: t("alexandria"), href: "/destinations/alexandria" },
            ]}
          />
          <FooterCol
            title={t("exploreTitle")}
            links={[
              // "Communities" = the /compounds hub ("Projects & communities"),
              // which links every community page, so none is orphaned.
              { label: t("communities"), href: "/compounds" },
              { label: t("marassi"), href: "/compounds/marassi" },
              { label: t("foukaBay"), href: "/compounds/fouka-bay" },
              { label: t("allHomes"), href: "/search" },
              { label: t("experiences"), href: "/experiences" },
              { label: t("guides"), href: "/guides" },
              { label: t("priceIndex"), href: "/north-coast-price-index" },
              { label: t("priceIndexCairo"), href: "/cairo-price-index" },
              { label: t("priceIndexElGouna"), href: "/el-gouna-price-index" },
              { label: t("priceIndexAlexandria"), href: "/alexandria-price-index" },
            ]}
          />
          <FooterCol
            title={t("companyTitle")}
            links={[
              { label: t("about"), href: "/about" },
              { label: t("furnish"), href: "/furnish" },
              { label: t("contact"), href: "/contact" },
            ]}
          />
          <FooterCol
            title={t("trustTitle")}
            links={[
              { label: t("howVerification"), href: "/verification" },
              { label: tc("trustSafety"), href: "/trust" },
              { label: t("faq"), href: "/faq" },
            ]}
          />
        </div>
        <div className="border-t border-white/10 pt-5 flex flex-col md:flex-row justify-between items-start md:items-center text-xs gap-3">
          <span>{copyright}</span>
          <nav className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <Link href="/terms" className="hover:text-white">
              {t("terms")}
            </Link>
            <Link href="/privacy" className="hover:text-white">
              {t("privacy")}
            </Link>
            <Link href="/refund-policy" className="hover:text-white">
              {t("refundPolicy")}
            </Link>
            <Link href="/delivery-policy" className="hover:text-white">
              {t("deliveryPolicy")}
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}

function AppleIcon() {
  return (
    <svg width="16" height="20" viewBox="0 0 14 17" fill="white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.76 9.03c-.02-2.07 1.7-3.07 1.78-3.12-1-1.44-2.52-1.63-3.05-1.65-1.29-.13-2.54.77-3.2.77-.67 0-1.69-.76-2.78-.74C2.97 4.31 1.3 5.65.52 7.56c-1.58 3.87.4 9.57 1.13 12.7.76 1.1 1.66 2.33 2.84 2.28 1.15-.05 1.58-.73 2.97-.73 1.38 0 1.78.73 2.97.71 1.23-.02 2.01-1.11 2.75-2.21.88-1.27 1.24-2.5 1.26-2.57-.03-.01-2.41-.92-2.43-3.67l-.25-.04zM9.6 2.74C10.28 1.91 10.73.8 10.6-.35c-.97.04-2.15.65-2.85 1.46-.62.71-1.17 1.85-1.03 2.94 1.09.08 2.2-.55 2.88-1.31z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5.5" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" />
    </svg>
  );
}

function TikTokIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.5 3c.3 2.1 1.6 3.6 3.7 3.9v2.4c-1.3.1-2.5-.3-3.7-1v5.6c0 3.2-2.4 5.6-5.5 5.6S5.5 17.1 5.5 14s2.5-5.4 5.6-5.3v2.5c-.4-.1-.8-.2-1.2-.2-1.6 0-2.9 1.3-2.9 3s1.3 3 2.9 3 2.9-1.3 2.9-3V3h3.2z" />
    </svg>
  );
}

function FooterCol({
  title,
  links,
}: {
  title: string;
  links: { label: string; href: string }[];
}) {
  return (
    <div>
      {/* h2, not h4: these are the only headings below the page's h1 in the
          footer, so h4 skipped two levels on every page on the site. Same
          classes — identical rendering, valid outline. */}
      <h2 className="text-white font-bold text-[13px] uppercase tracking-wider mb-3.5">
        {title}
      </h2>
      <ul className="flex flex-col gap-2.5 text-sm">
        {links.map((l) => (
          <li key={l.href}>
            <Link href={l.href} className="hover:text-white">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
